import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, Pause, CheckCircle2, Circle, MessageSquare } from "lucide-react";

import { tp } from "@/lib/plural";
type AgentStatus = "Not Started" | "In Progress" | "Complete" | "Paused";

const statusConfig: Record<AgentStatus, { icon: React.ElementType; label: string; className: string }> = {
  "Not Started": { icon: Circle, label: "Not Started", className: "bg-muted text-muted-foreground" },
  "In Progress": { icon: Activity, label: "Running", className: "bg-info/10 text-info border-info/20" },
  "Paused": { icon: Pause, label: "Paused — Awaiting Consultant", className: "bg-warning/10 text-warning border-warning/20" },
  "Complete": { icon: CheckCircle2, label: "Complete", className: "bg-success/10 text-success border-success/20" },
};

interface AgentStatusBannerProps {
  status: AgentStatus;
  agentName: string;
  detail?: string;
  pendingCount?: number;
  waitingFor?: string;
  onReviewClick?: () => void;
}

export function AgentStatusBanner({ status, agentName, detail, pendingCount, waitingFor, onReviewClick }: AgentStatusBannerProps) {
  const { t } = useTranslation();
  const config = statusConfig[status];
  const Icon = config.icon;
  const isPaused = status === "Paused";

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${config.className}`}>
      <Icon className={`h-4 w-4 shrink-0 ${isPaused ? "animate-pulse" : ""}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{agentName}</span>
          <Badge variant="outline" className={`text-[10px] ${config.className}`}>{t(config.label)}</Badge>
          {isPaused && pendingCount && (
            <Badge variant="outline" className="text-[10px] bg-warning/20 text-warning border-warning/30 animate-pulse">
              {tp(t, pendingCount, "{{n}} decision pending", "{{n}} decisions pending")}
            </Badge>
          )}
        </div>
        {detail && <p className="text-xs opacity-80 mt-0.5 truncate">{detail}</p>}
        {isPaused && waitingFor && (
          <p className="text-xs mt-1 font-medium">
            ⏸ {t("Waiting for:")} <span className="opacity-80">{waitingFor}</span>
          </p>
        )}
      </div>
      {isPaused && onReviewClick && (
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 text-xs gap-1.5 border-warning/30 text-warning hover:bg-warning/10 hover:text-warning"
          onClick={onReviewClick}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {t("Review & Resume")}
        </Button>
      )}
    </div>
  );
}
